import React, { useRef } from 'react';
import _ from 'lodash';
import { MdSave, MdFileDownload } from 'react-icons/md';
import Crud from '~/components/crud';
import { formats } from '~/helper';
import confirm from '~/components/confirm';
import { PersonForm, PersonSchema } from '~/views/persons/form';
import { Container, HiddenInput } from '~/views/persons/styles';
import usePersons from '~/view-data/use-persons';
import CellStatus from '~/components/datatable/cell-status';
import FloatingButtonGroup from '~/components/floatbutton/group';
import ImportModal from '~/views/persons/modal-import';

const columns = [
  {
    name: '# Nome',
    selector: 'name',
    cell: (row) => {
      return (
        <CellStatus title={row.name} color={row.active ? '#53d449' : '#e6467f'}>
          <strong>{row.name}</strong>
        </CellStatus>
      );
    },
  },
  {
    name: 'CPF/Número de Registro',
    selector: 'identify',
    format: (row) => formats.cpf(_.get(row, 'identify')) || _.get(row, 'identify') || ''
  },
  {
    name: 'Função',
    selector: 'role',
    hide: 'md',
    format: (row) => _.get(row, 'role.name')
  },
  {
    name: 'Email',
    selector: 'email',
    hide: 'md',
  },
  {
    name: 'Cadastrado em',
    selector: 'createdAt',
    hide: 'md',
    width: '140px',
    format: (row) => formats.dateTimeZone(_.get(row, 'createdAt'), 'dd/MM/yyyy HH:mm')
  },
];

function Persons({ acls }) {
  const canWrite = acls.includes('W');
  const { state, actions } = usePersons();
  const inputRef = useRef(null);

  const handleExportData = async () => {
    const result = await confirm.show({
      title: 'Atenção',
      text: `Deseja realmente exportar uma planilha com todos colaboradores?`,
    });

    if (result) {
      actions.exportSpreadsheet();
    }
  };

  const handleExportErrors = async (data) => {
    const result = await confirm.show({
      title: 'Atenção',
      text: `Deseja realmente exportar uma planilha com os erros?`,
    });

    if (result) {
      actions.exportSpreadsheetErrors(data);
    }
  };

  const handleImportData = () => {
    if (_.get(inputRef, 'current')) {
      inputRef.current.value = null;
      inputRef.current.click();
    }
  };

  let floatingActions = [
      {
        label: 'Importar Planilha',
        icon: 'fa fa-upload',
        action: handleImportData,
      },
      {
        label: 'Baixar Planilha',
        icon: 'fa fa-download',
        action: handleExportData
      }
    ];

  if (canWrite) {
    floatingActions.push({
      label: 'Novo colaborador',
      icon: 'fa fa-plus',
      action: () => actions.load({})
    });
  }

  return (
    <Container>
      {(!state.formOpen && !state.showImportModal) &&
        <FloatingButtonGroup
          anchorIcon="fa fa-ellipsis-v"
          anchorTitle="Ações"
          actions={floatingActions}
          />
      }
      <Crud
        columns={columns}
        openForm={state.formOpen}
        useOpenForm={true}
        emptyText='Nenhum colaborador encontrado'
        data={state.data}
        hideAdd={true}
        tableLoading={state.loading}
        formLoading={state.formLoading || false}
        formTitle={(data) => (_.get(data, 'id')) ? `Colaborador #${data.id}` : `Novo colaborador`}
        onChangePage={actions.pageChange}
        onRowClicked={actions.load}
        onCloseFilter={actions.formClose}
        formOptions={{
          validationSchema: PersonSchema(state.fromGroup, state.isPress),
          initialValues: state.model,
          initialTouched: { identify: true },
        }}
        renderForm={(args) => <PersonForm 
            {...args}
            isPress={state.isPress}
            fromGroup={state.fromGroup}
            onListRoles={actions.listRoles}
            previewMode={!canWrite} />}
        actions={[
          {
            label: 'Salvar',
            icon: MdSave,
            isSubmit: true,
            isDisabled: ({ isValid }) => !isValid || !canWrite,
            action: actions.createOrUpdate,
          },
        ]}
        extraActions={[
          {
            label: 'Exportar',
            right: 90,
            icon: MdFileDownload,
            action: handleExportData
          },
        ]}
      />
      <HiddenInput
        ref={inputRef}
        type="file"
        accept=".xls,.xlsx,.csv"
        onChange={(event) => actions.importSpreadsheet(event.target.files)}
        />

      <ImportModal
        loading={state.modalLoading}
        data={state.modalData || {}}
        handleOnSubmit={actions.saveImportModal}
        handleExportErrors={handleExportErrors}
        isOpen={state.showImportModal}
        closeModal={actions.closeImportModal}
        title={<>Importação de colaboradores</>}
        />
    </Container>
  );
}

export default Persons;
